import React, { useState, useEffect } from "react";
import { Form, Select, Typography, DatePicker } from "antd";
import { useTranslation } from "react-i18next";
import axios from "axios";
import dayjs from 'dayjs';
import { elvis } from "../../Utils";

const { Option } = Select;
const { Title, Text } = Typography;

const BusinessOperationsDamageForm = props => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const isEditMode = props.isEditMode || false;
  const [disruptionTypes, setDisruptionTypes] = useState([]);
  const [disruptionLevels, setDisruptionLevels] = useState([]);

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/business/enums/disruption-types")
      .then(({ data }) => setDisruptionTypes(data))
      .catch(error => console.log(error));
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/business/enums/disruption-levels")
      .then(({ data }) => setDisruptionLevels(data))
      .catch(error => console.log(error));
  }, []);

  useEffect(() => {
    if (props.record.damage) {
      form.setFieldsValue({
        disruptionType: props.record.damage.disruptionType,
        disruptionLevel: props.record.damage.disruptionLevel,
        startDate: props.record.damage.startDate ? dayjs(props.record.damage.startDate) : null,
        endDate: props.record.damage.endDate ? dayjs(props.record.damage.endDate) : null
      });
      if (isEditMode) {
        const newDamage = { ...props.record };
        newDamage.valid = Boolean(props.record.damage.disruptionType);
        props.parentCallback(newDamage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.record.damage, form, isEditMode]);

  const onValuesChange = (changedValues, allValues) => {
    if (!isEditMode) return;
    const newDamage = { ...props.record };
    newDamage.damage.disruptionType = elvis(allValues.disruptionType);
    newDamage.damage.disruptionLevel = elvis(allValues.disruptionLevel);
    newDamage.damage.startDate = elvis(allValues.startDate);
    newDamage.damage.endDate = elvis(allValues.endDate);
    form.validateFields()
      .then(() => {
        newDamage.valid = true;
        props.parentCallback(newDamage);
      })
      .catch((errorInfo) => {
        // A superseded validateFields() call rejects with empty errorFields (outOfDate);
        // only a non-empty errorFields is a real validation failure.
        const hasRealErrors = Array.isArray(errorInfo?.errorFields) && errorInfo.errorFields.length > 0;
        newDamage.valid = !hasRealErrors;
        props.parentCallback(newDamage);
      });
  };

  return (
    <Form
      form={form}
      layout="horizontal"
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      className="details_form"
      onValuesChange={onValuesChange}
      name="businessOperationsDamageForm"
    >
      <Title className="details_form_header" title="Damages details" subTitle="Production & logistics" />
      <Form.Item
        label="Type of disruption"
        name="disruptionType"
        rules={isEditMode ? [{ required: true, message: 'Please select the type of disruption!' }] : []}
      >
        <Select disabled={!isEditMode}>
          {disruptionTypes.map(name => (
            <Option key={name} value={name}>{t('businessEnums.disruptionType.' + name, name)}</Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item label="Level" name="disruptionLevel">
        <Select disabled={!isEditMode} allowClear>
          {disruptionLevels.map(name => (
            <Option key={name} value={name}>{t('businessEnums.disruptionLevel.' + name, name)}</Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item label="Start date and time" name="startDate">
        <DatePicker showTime disabled={!isEditMode} />
      </Form.Item>
      <Form.Item label="End date and time" name="endDate">
        <DatePicker showTime disabled={!isEditMode} />
      </Form.Item>
      <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
        <Text type="secondary">The monetary effect of the interruption is recorded in the Economic Loss tab.</Text>
      </Form.Item>
    </Form>
  );
};

export default BusinessOperationsDamageForm;
